import { useQuery } from "@tanstack/react-query";
import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";
import { Department } from "@/types";
import { api } from "@/lib/api-typed";
import type { Schemas } from "@/types/api";
import { fromApi } from "@/types/api.codec";

const mapDepartment = (payload: Schemas["DepartmentRead"]): Department => fromApi("DepartmentRead", payload);

export const useDepartments = () => {
  const {
    data,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["departments"],
    queryFn: async () => {
      const payload = await api.get("/departments/");
      return payload.map(mapDepartment);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });

  const { mutateAsync: createDepartment, isPending: isCreating } = useMutationWithToast({
    mutationFn: (input: { name: string; description?: string; lead: string }) =>
      api.post("/departments/", { body: {
          name: input.name,
          description: input.description ?? "",
          lead: input.lead,
        } }),
    invalidateQueries: [["departments"]],
  });

  const { mutateAsync: updateDepartment, isPending: isUpdating } = useMutationWithToast({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; description?: string; lead?: string } }) =>
      api.patch("/departments/{department_id}", { path: { department_id: id }, body: data }),
    invalidateQueries: [["departments"]],
  });

  const { mutateAsync: deleteDepartment, isPending: isDeleting } = useMutationWithToast({
    mutationFn: (departmentId: string) =>
      api.delete("/departments/{department_id}", { path: { department_id: departmentId } }),
    invalidateQueries: [["departments"]],
  });

  const { mutateAsync: updateDepartmentMenuPaths, isPending: isUpdatingMenuPaths } = useMutationWithToast({
    mutationFn: ({ departmentId, menuPaths }: { departmentId: string; menuPaths: string[] }) =>
      api.put("/departments/{department_id}/menu-paths", { path: { department_id: departmentId }, body: { menu_paths: menuPaths } }),
    invalidateQueries: [["departments"]],
  });

  const { mutateAsync: addRepresentative, isPending: isAddingRepresentative } = useMutationWithToast({
    mutationFn: ({ deptId, userId }: { deptId: string; userId: string }) =>
      api.post("/departments/{department_id}/representatives", { path: { department_id: deptId }, body: { user_id: userId } }),
    invalidateQueries: [["departments"]],
  });

  const { mutateAsync: removeRepresentative, isPending: isRemovingRepresentative } = useMutationWithToast({
    mutationFn: ({ deptId, userId }: { deptId: string; userId: string }) =>
      api.delete("/departments/{department_id}/representatives/{user_id}", { path: { department_id: deptId, user_id: userId } }),
    invalidateQueries: [["departments"]],
  });

  // Atribuir pessoas a um departamento edita a ficha delas: por isso refresca
  // também a lista de colaboradores, que é de onde a ficha lê os departamentos.
  // (addMembers e removeMember invalidam os mesmos três grupos de queries.)

  const { mutateAsync: addMembers, isPending: isAddingMembers } = useMutationWithToast({
    mutationFn: ({ deptId, userIds }: { deptId: string; userIds: string[] }) =>
      api.post("/departments/{department_id}/members", { path: { department_id: deptId }, body: { user_ids: userIds } }),
    invalidateQueries: [["departments"], ["users"], ["career-events"]],
  });

  const { mutateAsync: removeMember } = useMutationWithToast({
    mutationFn: ({ deptId, userId }: { deptId: string; userId: string }) =>
      api.delete("/departments/{department_id}/members/{user_id}", { path: { department_id: deptId, user_id: userId } }),
    invalidateQueries: [["departments"], ["users"], ["career-events"]],
  });

  const fetchDepartmentMenuPaths = async (departmentId: string): Promise<string[]> => {
    const payload = await api.get("/departments/{department_id}/menu-paths", { path: { department_id: departmentId } });
    return payload ?? [];
  };

  return {
    departments: data ?? [],
    isLoading: isLoading || isFetching,
    isCreating,
    isUpdating,
    isDeleting,
    isUpdatingMenuPaths,
    isAddingRepresentative,
    isRemovingRepresentative,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    updateDepartmentMenuPaths,
    fetchDepartmentMenuPaths,
    addRepresentative,
    removeRepresentative,
    addMembers,
    isAddingMembers,
    removeMember,
  };
};

